import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, getCurrentBrand } from '../config/api';
import { heicTo } from 'heic-to';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Stack,
  ImageList,
  ImageListItem,
  Paper,
  Divider,
  Avatar,
  Chip,
  Pagination
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';

/** Square output; server maps to Replicate `aspect_ratio` 1:1 */
const XHS_AI_IMAGE_SIZE = '1024x1024';
const MAX_AI_REFERENCE_IMAGES = 14;

const XHSAIImageGenerator = () => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  /** Ordered reference images: { url, id } id from store when known */
  const [selectedRefs, setSelectedRefs] = useState([]);
  const [generatedImageUrl, setGeneratedImageUrl] = useState('');
  const [images, setImages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadImagesPage = useCallback(async (targetPage) => {
    try {
      setLoadingImages(true);
      setError(null);
      const limit = 10;
      const offset = (targetPage - 1) * limit;
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        brand: getCurrentBrand()
      });
      const response = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?${params}`);
      if (!response.ok) {
        throw new Error('Failed to load images');
      }
      const data = await response.json();
      setImages(data.images || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingImages(false);
    }
  }, []);

  useEffect(() => {
    loadImagesPage(page);
  }, [page, loadImagesPage]);

  const handleUpload = async (event) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length === 0) return;

    const remainingSlots = MAX_AI_REFERENCE_IMAGES - selectedRefs.length;
    if (remainingSlots <= 0) {
      setError(
        t('aiGenerator.aiImageMaxSelected', { max: MAX_AI_REFERENCE_IMAGES }) ||
          `You can select at most ${MAX_AI_REFERENCE_IMAGES} images.`
      );
      event.target.value = '';
      return;
    }

    const toProcess = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      setError(
        t('aiGenerator.aiImageMaxUploadBatch', {
          count: remainingSlots,
          max: MAX_AI_REFERENCE_IMAGES
        }) ||
          `Only ${remainingSlots} more image(s) can be added (limit ${MAX_AI_REFERENCE_IMAGES}).`
      );
    } else {
      setError(null);
    }

    try {
      setUploading(true);
      setSuccess(null);

      let lastCreated = null;
      for (const file of toProcess) {
        let uploadFile = file;

        if (file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
          const blob = await heicTo({ blob: file, type: 'image/jpeg' });
          const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
          uploadFile = new File([blob], newName, { type: 'image/jpeg' });
        }

        const formData = new FormData();
        formData.append('image', uploadFile);
        const uploadResponse = await fetch(API_ENDPOINTS.UPLOAD_TO_R2, {
          method: 'POST',
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image');
        }

        const uploadResult = await uploadResponse.json();

        const insertResponse = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?brand=${getCurrentBrand()}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_url: uploadResult.url,
            is_active: true
          })
        });

        if (!insertResponse.ok) {
          throw new Error(
            t('aiGenerator.aiImageUploadSaveFailed') || 'Image uploaded but failed to save to XHS library'
          );
        }

        const saveData = await insertResponse.json();
        const created = saveData.image;
        lastCreated = created;
        const url = created?.image_url || uploadResult.url;
        const id = created?.id ?? null;
        setSelectedRefs((prev) => (prev.some((r) => r.url === url) ? prev : [...prev, { url, id }]));
      }

      if (lastCreated) {
        setSuccess(t('aiGenerator.aiImageUploadSaved') || 'Image saved to XHS library');
      }
      if (files.length <= remainingSlots) {
        setError(null);
      }

      setPage(1);
      await loadImagesPage(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const toggleSelectImage = (image) => {
    const url = image.image_url;
    setSelectedRefs((prev) => {
      const exists = prev.some((r) => r.url === url);
      if (exists) return prev.filter((r) => r.url !== url);
      if (prev.length >= MAX_AI_REFERENCE_IMAGES) return prev;
      return [...prev, { url, id: image.id }];
    });
  };

  const clearSelection = () => {
    setSelectedRefs([]);
    setError(null);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError(t('aiGenerator.aiImageMissingPrompt') || 'Please add a prompt');
      return;
    }
    if (selectedRefs.length === 0) {
      setError(t('aiGenerator.aiImageMissingImage') || 'Please select or upload at least one image');
      return;
    }
    const imageUrls = selectedRefs.map((r) => r.url).filter(Boolean);
    if (imageUrls.some((u) => /\.heic($|\?)/i.test(u))) {
      setError(t('aiGenerator.aiImageHeicBlocked') || 'Please re-upload this image as JPG or PNG');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      setSuccess(null);
      setGeneratedImageUrl('');

      const response = await fetch(`${API_ENDPOINTS.GENERATE_XHS_AI_IMAGE}?brand=${getCurrentBrand()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrls,
          prompt: prompt.trim(),
          size: XHS_AI_IMAGE_SIZE
        })
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const msg =
          result.message ||
          result.error ||
          t('aiGenerator.aiImageGenerateFailed') ||
          'Failed to generate image';
        throw new Error(msg);
      }

      if (!result.success || !result.url) {
        throw new Error('AI image generation failed');
      }

      setGeneratedImageUrl(result.url);
      setSuccess(t('aiGenerator.aiImageGenerated') || 'AI image generated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 4,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
          color: 'white',
          position: 'relative',
          boxShadow: '0 16px 40px rgba(44, 62, 80, 0.25)'
        }}
      >
        <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Avatar sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              width: { xs: 56, sm: 64 },
              height: { xs: 56, sm: 64 },
              border: '3px solid rgba(255,255,255,0.35)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
            }}>
              <ImageIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'white' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h3" sx={{
                fontWeight: 900,
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2.1rem', md: '2.6rem' },
                letterSpacing: '-0.02em',
                textShadow: '0 6px 24px rgba(0,0,0,0.25)'
              }}>
                {t('aiGenerator.aiImageTitle') || 'AI Image Generator'}
              </Typography>
              <Typography variant="body1" sx={{
                opacity: 0.95,
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}>
                {t('aiGenerator.aiImageSubtitleMulti') ||
                  'Select one or more XHS images (or upload), then describe the poster you want'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Chip label="Step 1: Select image(s)" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
              <Chip label="Step 2: Prompt" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
              <Chip label="Step 3: Generate" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {(error || success) && (
        <Box sx={{ mb: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 3, boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Chip label="1" color="secondary" size="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('aiGenerator.aiImageSelectImages') || 'Select or upload images'}
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              {t('aiGenerator.aiImageMultiHint', {
                count: selectedRefs.length
              }) ||
                `Tap thumbnails to toggle selection. ${selectedRefs.length} selected.`}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
              <Button
                variant="contained"
                component="label"
                startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                disabled={uploading}
                sx={{ borderRadius: 2, px: 3, boxShadow: '0 6px 16px rgba(255, 107, 107, 0.25)' }}
              >
                {t('aiGenerator.aiImageUpload') || 'Upload'}
                <input hidden accept="image/*" multiple type="file" onChange={handleUpload} />
              </Button>
              <Button variant="text" size="small" disabled={selectedRefs.length === 0} onClick={clearSelection}>
                {t('aiGenerator.aiImageClearSelection') || 'Clear selection'}
              </Button>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('aiGenerator.aiImageChooseFromLibrary') || 'Or toggle images from your XHS library below'}
              </Typography>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {loadingImages ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">
                  {t('aiGenerator.loadingXHSImages') || 'Loading XHS images...'}
                </Typography>
              </Stack>
            ) : images.length === 0 ? (
              <Stack spacing={1} alignItems="center" sx={{ py: 3 }}>
                <ImageIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('aiGenerator.noXHSImagesAvailable') || 'No XHS images available'}
                </Typography>
              </Stack>
            ) : (
              <ImageList
                cols={4}
                gap={16}
                sx={{
                  gridTemplateColumns: {
                    xs: 'repeat(2, 1fr) !important',
                    sm: 'repeat(3, 1fr) !important',
                    md: 'repeat(4, 1fr) !important'
                  }
                }}
              >
                {images.map((image) => {
                  const selIndex = selectedRefs.findIndex((r) => r.url === image.image_url);
                  const isSelected = selIndex >= 0;
                  const orderBadge = selIndex + 1;
                  return (
                  <ImageListItem
                    key={image.id}
                    onClick={() => toggleSelectImage(image)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: isSelected ? '3px solid #ff6b6b' : '2px solid rgba(0,0,0,0.04)',
                      position: 'relative',
                      boxShadow: isSelected ? '0 12px 25px rgba(255, 107, 107, 0.25)' : '0 6px 16px rgba(0,0,0,0.08)',
                      transition: 'all 0.2s ease',
                      '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 10px 24px rgba(0,0,0,0.12)' }
                    }}
                  >
                    <Box
                      component="img"
                      src={image.image_url}
                      alt={`XHS ${image.id}`}
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: '100%',
                        height: { xs: 140, sm: 180, md: 200, lg: 220 },
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                        '&:hover': { transform: 'scale(1.03)' }
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <Box
                      sx={{
                        width: '100%',
                        height: { xs: 140, sm: 180, md: 200, lg: 220 },
                        display: 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        flexDirection: 'column'
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 24, color: '#ccc' }} />
                    </Box>
                    {isSelected && (
                      <Box sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: '#ff6b6b',
                        color: 'white',
                        borderRadius: '50%',
                        minWidth: 24,
                        height: 24,
                        px: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: 12
                      }}>
                        {orderBadge}
                      </Box>
                    )}
                  </ImageListItem>
                  );
                })}
              </ImageList>
            )}
            {totalCount > 10 && (
              <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
                <Pagination
                  count={Math.ceil(totalCount / 10)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                />
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 3, mb: 3, boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Chip label="2" color="secondary" size="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('aiGenerator.aiImagePromptLabel') || 'Prompt'}
              </Typography>
            </Stack>
            <TextField
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              placeholder={t('aiGenerator.aiImagePromptPlaceholder') || 'e.g., Create a modern food poster with bold title and price'}
            />
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
              sx={{ mt: 2, borderRadius: 2, py: 1.2, boxShadow: '0 10px 24px rgba(255, 107, 107, 0.3)' }}
              fullWidth
              disabled={generating}
              onClick={handleGenerate}
            >
              {t('aiGenerator.aiImageGenerate') || 'Generate AI Poster'}
            </Button>
          </Paper>

          <Paper sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 3, boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Chip label="3" color="secondary" size="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('aiGenerator.aiImagePreview') || 'Preview'}
              </Typography>
            </Stack>
            {generatedImageUrl ? (
              <Box
                component="img"
                src={generatedImageUrl}
                alt="Generated"
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  border: '1px solid #eee',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.12)'
                }}
              />
            ) : (
              <Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
                <ImageIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('aiGenerator.aiImagePreviewEmpty') || 'Your generated image will appear here'}
                </Typography>
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default XHSAIImageGenerator;
